import { useRef } from 'react';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import TenantPage from 'layouts/TenantPage';
import { filterOperators } from 'components/enhanced/data-grid/utils/filters';
import { Link } from 'react-router-dom';
import DataGridCell from 'components/enhanced/data-grid/components/cell/DataGridCell';
import { useLocation } from 'react-router-dom';
import { unapprovedMovementService } from '../api/unapproved-movement.service';
import { baseCell } from 'components/cells/BaseCell';

export const UnapprovedMovementsDDT = () => {
  const translationPrefix = 'pages.movements.unapproved';
  const { t } = useTranslation();
  const datagridRefresh = useRef(null);

  const columns: GridColDef[] = [
    {
      field: 'transport_document_code',
      headerName: t(`entities.movement.transport_document`),
      filterOperators,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const location = useLocation();
        const basePathRaw = location.pathname;
        const basePath = basePathRaw.endsWith('/') ? basePathRaw.slice(0, -1) : basePathRaw;

        const transport_document_code = params.row.transport_document_code;
        const transport_document_date = params.row.transport_document_date;
        const warehouse = params.row.warehouse;
        const order_ref = params.row.order_ref;

        const hasCode = !!transport_document_code;

        let url = '';
        if (hasCode) {
          // If transport_document_code exists, use the code as path param
          const encodedCode = `/${encodeURIComponent(transport_document_code)}`;
          const query = order_ref ? `?order_ref=${encodeURIComponent(order_ref)}` : '';
          url = `${basePath}${encodedCode}${query}`;
        } else {
          // If no transport_document_code, build query params with all available fields
          const queryParams = new URLSearchParams();

          // Add the noDDT flag to indicate no transport document code
          queryParams.append('noDDT', 'true');

          queryParams.append('transport_document_date', transport_document_date);

          queryParams.append('warehouse', warehouse);

          if (order_ref) {
            queryParams.append('order_code', order_ref);
          }

          url = `${basePath}/aggregated/?${queryParams.toString()}`;
        }

        return (
          <div>
            <Link to={url}>
              <b className="text-secondary-500">{transport_document_code || 'No DDT'}</b>
            </Link>
          </div>
        );
      }
    },
    {
      field: 'transport_document_date',
      headerName: t(`entities.movement.transport_document_date`),
      flex: 1,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'suppliers',
      headerName: t(`global.suppliers`),
      flex: 1,
      filterOperators,
      renderCell: (params: GridRenderCellParams<any, string[]>) => (
        <DataGridCell content={params.row.transport_document_code ? params.value.join(', ') : ''} />
      )
    },
    {
      field: 'warehouse',
      headerName: t(`global.warehouse`),
      flex: 1,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'order_ref',
      headerName: t(`global.order`),
      flex: 1,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'created_by',
      headerName: t(`entities.movement.created_by`),
      filterable: true,
      filterOperators,
      sortable: true,
      flex: 1,
      renderCell: (params: GridRenderCellParams<string[]>) => (
        <DataGridCell content={params.value ? params.value.join(', ') : ''} />
      )
    }
  ];

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}>
      <EnhancedDataGrid
        columns={columns}
        service={unapprovedMovementService}
        getMethod={unapprovedMovementService.aggregatedDdt}
        warehouseFilterParam="warehouse"
        refresh={datagridRefresh}
      />
    </TenantPage>
  );
};

export default UnapprovedMovementsDDT;
